import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import KpiCard from '../components/KpiCard.jsx';
import DateRangePicker, { rangeForDays } from '../components/DateRangePicker.jsx';
import TrendChart from '../components/TrendChart.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import StatusBadge from '../components/StatusBadge.jsx';

/**
 * Revenue rendering, rewritten 2026-09-12.
 *
 * This page used to print whatever currencies the API handed it, which meant it printed
 * `INR 58,308.19` for a day the demand partners reported `USD 0.010775` on. That figure was a sum
 * of bid CPMs booked once per impression and recognised at auction-win time -- Kunal's description
 * of it when he asked for this fix: "that too fake one which does not belong to us."
 *
 * The API now serves the partner-reported USD restatement, so the rendering has a job it did not
 * have before: keeping the revenue STATES visually distinct. `revenueStatus` is not decoration --
 * a pending day and a confirmed zero are different facts, and printing both as "$0.00" is exactly
 * the mistake this whole change exists to stop.
 */

/**
 * Real figures here are currently in thousandths of a dollar. At two decimal places $0.008620
 * renders as "$0.01"; at zero it renders as "$0.00", at which point an operator concludes the
 * pipeline is broken rather than that revenue is genuinely tiny. Precision scales with magnitude
 * so a real $4,210.55 does not grow six meaningless digits.
 */
function formatUsd(amount) {
  // null/undefined FIRST, and not via Number(): `Number(null)` is 0, so a `!Number.isFinite`
  // guard alone would render an absent figure as "$0.00" -- unknown is not zero, which is the
  // whole point of this change.
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const digits = abs === 0 ? 2 : abs < 0.01 ? 6 : abs < 1 ? 4 : 2;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

/**
 * `revenueStatus` from the API (config-backend's lib/restatedRevenue.js):
 *   partner_reported  confirmed against the partner's own reporting API
 *   no_wins           a real, confirmed zero
 *   partial           some partners reported, at least one has not
 *   pending           nobody has reported yet -- NOT zero
 *   evidence_expired  the auction rows are past retention; the day cannot be recomputed at all
 *   no_data           outside the restated range
 *
 * 'pending' and 'no_data' render as a word, never a number. A dash or a zero in this cell reads as
 * "we earned nothing", which is a claim we cannot make yet.
 *
 * 'evidence_expired' (migration 075) gets its OWN word rather than borrowing 'Pending'. An
 * operator reading "Pending" goes and chases the partner; there is no partner to chase, and the
 * only thing that recovers the day is a database restore. Rendering the two the same sends
 * someone on an errand that cannot succeed.
 */
function formatRevenue(byCurrency, status) {
  if (status === 'evidence_expired') return 'Expired';
  if (status === 'pending' || status === 'no_data' || status === 'overdue') return 'Pending';
  const amount = (byCurrency || {}).USD;
  if (amount === undefined || amount === null) return status === 'no_wins' ? formatUsd(0) : 'Pending';
  return formatUsd(amount);
}

/**
 * A short qualifier beside a figure that is not final. Nothing at all when it is.
 *
 * 'overdue' (2026-09-13) is the one that asks for action rather than patience: the reporting sync
 * runs hourly, so a day with wins and no figure a full day after it closed is a partner who has
 * not reported, not a slow pipeline.
 */
function revenueNote(status) {
  if (status === 'partial') return 'partial — not every partner has reported';
  if (status === 'overdue') return 'overdue — partner has not reported';
  if (status === 'pending') return 'no partner figure yet — not zero';
  if (status === 'no_data') return 'no partner figure yet';
  if (status === 'evidence_expired') return 'auction rows past retention — restore from backup to recompute';
  return null;
}

function pct(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

export default function OverviewPage() {
  const [range, setRange] = useState({ ...rangeForDays(30), days: 30 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [partnerStats, setPartnerStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [ov, ps] = await Promise.all([
          api.overview(range.from, range.to),
          api.demandPartnerStats(range.from, range.to),
        ]);
        if (!cancelled) {
          setOverview(ov);
          setPartnerStats(ps);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load network stats.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to]);

  const totals = overview?.totals;
  const daily = overview?.daily || [];
  const byPublisher = overview?.byPublisher || [];
  const partners = [...(partnerStats?.partners || [])].sort((a, b) => (b.calls || 0) - (a.calls || 0));

  return (
    <div className="page">
      <PageHeader
        title="Overview"
        subtitle="Network performance across every publisher and demand partner."
        actions={<DateRangePicker from={range.from} to={range.to} activeDays={range.days} onChange={setRange} />}
      />

      {loading && <LoadingState label="Loading network stats" />}

      {!loading && error && (
        <EmptyState title="Couldn't load overview" message={error} />
      )}

      {!loading && !error && overview && (
        <>
          <div className="kpi-grid">
            <KpiCard label="Auctions" value={(totals?.auctions ?? 0).toLocaleString()} tone="accent" />
            <KpiCard label="Impressions" value={(totals?.imps ?? 0).toLocaleString()} />
            <KpiCard label="Wins" value={(totals?.wins ?? 0).toLocaleString()} />
            <KpiCard label="Bid win rate" value={totals?.bidWinRate === null || totals?.bidWinRate === undefined ? '—' : pct(totals.bidWinRate)} />
            {/* Net is what BidDigi keeps; gross is what the partners say they owe before our
                margin. Both shown, because an operator checking unit economics needs the margin
                and re-deriving it from two pages is how people stop checking. */}
            <KpiCard
              label="Revenue (net)"
              value={formatRevenue(totals?.revenueByCurrency, totals?.pendingDays > 0 ? 'partial' : 'partner_reported')}
              tone="accent"
            />
            <KpiCard label="Revenue (gross)" value={totals?.grossUsd === null || totals?.grossUsd === undefined ? 'Pending' : formatUsd(totals.grossUsd)} />
          </div>

          {/* The number changed basis on 2026-09-12 and an operator who remembers the old one
              needs to know why it fell by five orders of magnitude, not wonder if we broke
              something. This line is the whole explanation and it stays until it is common
              knowledge. */}
          <p className="page__note">
            Revenue is what the demand partners' own reporting APIs say they will pay, in USD, net of
            BidDigi's margin — not BidDigi's auction-side figure. Auction, impression and win counts
            are BidDigi's own measurement.
            {totals?.pendingDays > 0 && (
              <>
                {' '}
                <strong>
                  {totals.pendingDays} day{totals.pendingDays === 1 ? '' : 's'} in this range
                  {totals.pendingDays === 1 ? ' has' : ' have'} no final partner figure yet
                </strong>
                , so this total will still move. A pending day is not zero revenue. Partner reports
                sync hourly; anything still missing a day after it closed is marked overdue.
              </>
            )}
          </p>

          <section className="card">
            <div className="card__header">
              <h2 className="card__title">Auctions &amp; wins over time</h2>
            </div>
            {daily.length ? (
              <TrendChart
                data={daily}
                series={[
                  { key: 'auctions', label: 'Auctions', color: '#000002' },
                  { key: 'wins', label: 'Wins', color: '#ED2E5A' },
                ]}
              />
            ) : (
              <EmptyState title="No auction activity in this range" message="Try a wider date range once traffic starts flowing." />
            )}
          </section>

          <section className="card">
            <div className="card__header">
              <h2 className="card__title">Demand partner performance</h2>
              <span className="card__hint">Sorted by calls</span>
            </div>
            {partners.length ? (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Partner</th>
                      <th>Status</th>
                      <th className="num">Calls</th>
                      <th className="num">Responses</th>
                      <th className="num">Response rate</th>
                      <th className="num">Wins</th>
                      <th className="num">Win rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => (
                      <tr key={p.code}>
                        <td>
                          <div className="cell-title">{p.name}</div>
                          <div className="cell-subtitle">{p.code}</div>
                        </td>
                        <td>
                          <StatusBadge status={p.enabled ? 'active' : 'paused'} />
                        </td>
                        <td className="num">{(p.calls ?? 0).toLocaleString()}</td>
                        <td className="num">{(p.responses ?? 0).toLocaleString()}</td>
                        <td className="num">{pct(p.responseRate)}</td>
                        <td className="num">{(p.wins ?? 0).toLocaleString()}</td>
                        <td className="num">{pct(p.winRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No demand partner activity yet" />
            )}
          </section>

          <section className="card">
            <div className="card__header">
              <h2 className="card__title">Publisher breakdown</h2>
            </div>
            {byPublisher.length ? (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Publisher</th>
                      <th className="num">Auctions</th>
                      <th className="num">Impressions</th>
                      <th className="num">Wins</th>
                      <th className="num">Bid win rate</th>
                      <th className="num">Revenue (net USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byPublisher.map((p) => (
                      <tr key={p.publisherId}>
                        <td>
                          <div className="cell-title">{p.name}</div>
                          <div className="cell-subtitle">{p.domain}</div>
                        </td>
                        <td className="num">{(p.auctions ?? 0).toLocaleString()}</td>
                        <td className="num">{(p.imps ?? 0).toLocaleString()}</td>
                        <td className="num">{(p.wins ?? 0).toLocaleString()}</td>
                        <td className="num">{p.bidWinRate === null || p.bidWinRate === undefined ? '—' : pct(p.bidWinRate)}</td>
                        <td className="num">
                          {formatRevenue(p.revenueByCurrency, p.revenueStatus)}
                          {revenueNote(p.revenueStatus) && (
                            <div className="cell-subtitle">{revenueNote(p.revenueStatus)}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No publisher activity yet" message="Publisher breakdown will appear once auctions start recording." />
            )}
          </section>
        </>
      )}
    </div>
  );
}
